import { useRef, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { DatePicker, MyTimePicker } from './DatePicker';
import Desc from './Description';
import { SettingGroup, ValueBox } from './SettingGroup';
import { addZ } from '../Utils/utils';
import { controllerClockDisplayValues } from '../Utils/system_settings_utils';
import { useProxySelector } from '../Hooks/useProxySelector';

const formatDate = (v) => `${v.time_cont_year}-${addZ(v.time_cont_month)}-${addZ(v.time_cont_day)}`;
const formatTime = (v) => `${addZ(v.time_cont_hour)}:${addZ(v.time_cont_min)}:${addZ(v.time_cont_sec)}`;

// Shared by the global System editor and the historical Timing editor.
// With `liveController` (the controller proxy), an unedited date or time follows
// the controller RTC. Only the pickers subscribe, so the ticking clock does not
// re-render the surrounding tab.
export default function ControllerClock({ controller, liveController, values, disabled, resetKey,
  onDateChange, onTimeChange, onValidityChange }) {
  const { t } = useTranslation();
  return (
    <SettingGroup controller={controller} id={t('controller_time')}>
      <ValueBox controller={controller} id={'timing_controller_date'}>
        <Typography gutterBottom><Desc id="controller_date" name="Controller Date" /></Typography>
        <ClockDate key={resetKey} liveController={liveController} values={values}
          disabled={disabled} onChange={onDateChange} onValidityChange={onValidityChange} />
      </ValueBox>
      <ValueBox controller={controller} id={'timing_controller_time'}>
        <Typography gutterBottom><Desc id="controller_time" name="Controller Time" /></Typography>
        <ClockTime liveController={liveController} values={values}
          disabled={disabled} onChange={onTimeChange} />
      </ValueBox>
    </SettingGroup>
  );
}

// The live value while `follow`, otherwise the last one shown, so a pause (an
// invalid date being typed) does not jump back to the form's opening value.
function useLiveClockValue(liveController, follow, format) {
  const live = useProxySelector(liveController,
    (c) => (follow ? format(controllerClockDisplayValues(c)) : undefined), [follow]);
  const last = useRef();
  if (live !== undefined) last.current = live;
  return last.current;
}

function ClockDate({ liveController, values, disabled, onChange, onValidityChange }) {
  const { t } = useTranslation();
  const [valid, setValid] = useState(true);
  const live = useLiveClockValue(liveController, !values._date_changed && valid, formatDate);
  return (
    <DatePicker
      value={values._date_changed ? formatDate(values) : live ?? formatDate(values)}
      name="controller_date" inputProps={{ 'aria-label': t('controller_date') }}
      disabled={disabled} onChange={onChange}
      onValidityChange={(next) => {
        setValid(next);
        onValidityChange?.(next);
      }} />
  );
}

function ClockTime({ liveController, values, disabled, onChange }) {
  const live = useLiveClockValue(liveController, !values._time_changed, formatTime);
  return (
    <MyTimePicker
      value={values._time_changed ? formatTime(values) : live ?? formatTime(values)}
      seconds={true} name="controller_time" id="controller_time"
      disabled={disabled} onChange={onChange} />
  );
}
